<template>
  <div class="relative w-full flex flex-col border border-border-normal" style="border-radius: 10px;">
    <!-- Opener Button -->
    <button class="w-full flex items-center justify-between p-2.5"
    @click="isExpanded = !isExpanded" :disabled="loading">
      <div class="flex items-center gap-2">
        <MetaPlatformIcon v-if="platform === 'meta'" />
        <TikTokPlatformIcon v-if="platform === 'tiktok'" :width="16" :height="16" />
        <BaseText type="label" size="sm" class="text-text-muted">
          {{ platform === 'meta' ? 'Meta' : 'TikTok' }}
        </BaseText>
      </div>
      <div class="flex items-center gap-3">
        <transition>
          <BaseText v-if="selectedAccounts.length" type="body" size="xs" class="text-text-subdued">
            {{ selectedAccounts.length }} Selected
          </BaseText>
        </transition>
        <div class="relative">
          <transition name="opener-action">
            <BaseLoadingSpinnerCircle v-if="loading" :key="`platform-loading-${platform}`" :duration="1.2" class="text-icon-disabled" />
            <div v-if="!loading" :key="`platform-chevron-${platform}`" :class="{'pointer-events-none': loading}"
            class="transform transition-transform flex-shrink-0" :style="{ transform: isExpanded ? 'scaleY(-1)' : '' }">
              <ChevronIcon class="text-icon-normal" />
            </div>
          </transition>
        </div>
      </div>
    </button>
    <!-- Content -->
    <div ref="contentContainer" class="relative w-full">
      <transition name="content">
        <div v-if="isExpanded" class="w-full px-1">
          <!-- Connected State -->
          <div v-if="adAccountOptions.length" class="w-full flex flex-col">
            <!-- Authenticate New Account Button -->
            <AuthenticatePlatformButton 
              :platform="platform"
              :loading="loadingAuthURL"
              @click="$emit('authenticate')" 
            />
            <div class="w-full pl-1 pt-2">
              <AdAccountMultiSelector
                :ad-account-options="adAccountOptions"
                :selected-account-ids="selectedAccounts"
                :loading="loading"
                @select="handleSelect"
              />
            </div>
          </div>
          <!-- Not Connected State -->
          <div v-else class="w-full flex flex-col gap-1 pb-1">
            <div class="w-full flex flex-col gap-2 p-5 text-center">
              <BaseText type="label" size="sm" class="text-text-muted">
                Connect your {{ formattedPlatformName }} account to Foreplay.
              </BaseText>
              <BaseText type="body" size="sm" class="text-text-normal">
                Looks like you haven't connected any {{ formattedPlatformName }} accounts to Lens. Start creating reports for {{ formattedPlatformName }} ads with a few clicks.
              </BaseText>
            </div>
            <AuthenticatePlatformButton 
              :platform="platform"
              :loading="loadingAuthURL"
              is-first-account 
              @click="$emit('authenticate')" 
            />
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import smoothReflow from 'vue-smooth-reflow'
import EventBus from '@/plugins/BaseNotifications/EventBus'
import AuthenticatePlatformButton from './AuthenticatePlatformButton.vue'
import AdAccountMultiSelector from '../../AdAccountMultiSelector.vue'

// Icons
import MetaPlatformIcon from '../../../globals/Icons/FilterIcons/Platforms/MetaPlatformIcon.vue'
import TikTokPlatformIcon from '../../../globals/Icons/FilterIcons/Platforms/TikTokPlatformIcon.vue'
import ChevronIcon from '../../../globals/Icons/ChevronIcon.vue'
import SearchIcon from '../../../globals/Icons/SearchIcon.vue'

export default {
  name: 'DataSourceSelector',
  mixins: [smoothReflow],
  components: {
    AuthenticatePlatformButton,
    AdAccountMultiSelector,
    MetaPlatformIcon,
    TikTokPlatformIcon,
    ChevronIcon,
    SearchIcon
  },
  props: {
    platform: {
      type: String,
      required: true
    },
    adAccountOptions: {
      type: Array,
      required: true
    },
    selectedAccounts: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    loadingAuthURL: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isExpanded: false
    }
  },
  mounted () {
    EventBus.$on('tour:step-enter', this.handleTourStepEnter)
    this.$nextTick(() => {
      this.$smoothReflow({
        el: this.$refs.contentContainer,
        property: 'height',
        transition: 'height 300ms ease-in-out'
      })
    })
  },
  beforeDestroy () {
    EventBus.$off('tour:step-enter', this.handleTourStepEnter)
    this.$unsmoothReflow({ el: this.$refs.contentContainer })
  },
  computed: {
    formattedPlatformName () {
      return this.platform === 'meta' ? 'Meta' : 'TikTok'
    }
  },
  methods: {
    handleSelect (account) {
      this.$emit('select', account)
    },
    handleTourStepEnter ({ tourId, stepId } = {}) {
      if (tourId !== 'lens-connect') return
      if (['details', 'auth'].includes(stepId) && this.platform === 'meta') {
        this.isExpanded = true
      }
    }
  }
}
</script>

<style scoped>
.opener-action-enter-active, .opener-action-leave-active {
  transition: opacity 100ms ease-in-out;
}
.opener-action-leave-active {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.opener-action-enter-from, .opener-action-enter, .opener-action-leave-to {
  opacity: 0;
}
.opener-action-enter-to, .opener-action-leave-from {
  opacity: 1;
}
.content-enter-active, .content-leave-active {
  transition: opacity 300ms ease-in-out;
}
.content-leave-active {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.content-enter-from, .content-enter, .content-leave-to {
  opacity: 0;
}
.content-enter-to, .content-leave-from {
  opacity: 1;
}
.v-enter-active, .v-leave-active {
  transition: opacity 100ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>